import React from "react";
import RecognitionGuideLayout from "@/components/RecognitionGuideLayout";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { Award, Heart, Users, Target, MessageSquare, Trophy, ExternalLink, ArrowRight, Plus, Minus } from "lucide-react";

export { Page }

function Page() {
  const pillars = [
    {
      title: "Authentic",
      description: "Recognition should be genuine and heartfelt. Employees can spot 'check-the-box' praise from a mile away, and it often does more harm than good.",
      icon: Heart
    },
    {
      title: "Personalized", 
      description: "Different employees value different forms of recognition. Some thrive on public shoutouts, while others prefer a private thank-you or a tangible reward.",
      icon: Users
    },
    {
      title: "Equitable",
      description: "Recognition must be accessible to all employees, regardless of role, seniority, or location. Frontline workers, in particular, are often overlooked even though they make up 80% of the global workforce.",
      icon: Target
    },
    {
      title: "Embedded",
      description: "Recognition should be built into the flow of work, not bolted on as an afterthought. The most effective programs integrate directly into tools employees already use every day.",
      icon: MessageSquare
    },
    {
      title: "Frequent",
      description: "Once-a-year recognition doesn't cut it. Employees want acknowledgment on a regular basis, tied closely to the behaviors and outcomes you want to encourage.",
      icon: Trophy
    }
  ];

  const benefits = [
    "2.6x more productive",
    "9x more likely to feel a strong sense of belonging", 
    "3x more likely to say their company cares about their well-being"
  ];

  const examples = [
    "A peer giving a quick shoutout during a team meeting",
    "A manager celebrating progress during a weekly one-on-one",
    "An automated work anniversary or milestone celebration that highlights loyalty and commitment",
    "A company-wide recognition for embodying company values"
  ];

  return (
    <RecognitionGuideLayout
      title="What is Employee Recognition?"
      description="Learn what employee recognition means today and discover the five pillars of effective recognition that drive engagement, retention, and performance."
      currentStep={1}
    >
      <div className="space-y-12">
        {/* Header */}
        <div className="space-y-6">
          <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-primary/10 text-primary rounded-full text-sm font-medium border border-primary/20">
            <Award className="w-4 h-4" />
            Step 1 of 6
          </div>
          
          <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold text-foreground leading-tight">
            What is Employee Recognition?
          </h1>
          
          <div className="space-y-4">
            <p className="text-lg text-foreground leading-relaxed max-w-4xl">
              At its simplest, employee recognition is the act of acknowledging and celebrating the contributions of your people-whether it's hitting a sales target, solving a complex problem, helping a colleague, or consistently living your company values. Recognition is about letting employees know their work matters, their impact is seen, and their effort is valued.
            </p>
            
            <p className="text-muted-foreground leading-relaxed max-w-4xl">
              Traditionally, recognition was reserved for annual performance reviews or limited to "employee of the month" plaques. While those programs had good intentions, they often failed to deliver lasting impact. Employees today expect recognition that is real-time, personal, and consistent. They don't want to wait months for a token gesture-they want acknowledgment that feels relevant to their day-to-day work.
            </p>
            
            <p className="text-muted-foreground leading-relaxed max-w-4xl">
              This shift reflects a larger truth: in 2025, recognition is no longer just a feel-good HR practice. It's a strategic business lever that directly affects engagement, performance, and retention. In fact, according to{" "}
              <a 
                href="https://www.gallup.com/workplace/236441/employee-recognition-low-cost-high-impact.aspx" 
                target="_blank" 
                rel="noopener noreferrer"
                className="text-primary hover:text-primary/80 underline inline-flex items-center gap-1"
              >
                Gallup
                <ExternalLink className="w-3 h-3" />
              </a>, when recognition is done well, companies see higher productivity, stronger culture, and better business results.
            </p>
          </div>
        </div>

        {/* Five Pillars Section */}
        <section className="space-y-8">
          <div className="text-center space-y-3">
            <h2 className="text-2xl md:text-3xl font-bold text-foreground">
              The Five Pillars of Effective Recognition
            </h2>
            <p className="text-muted-foreground max-w-2xl mx-auto">
              Research from{" "}
              <a 
                href="https://www.gallup.com/workplace/236441/employee-recognition-low-cost-high-impact.aspx" 
                target="_blank" 
                rel="noopener noreferrer"
                className="text-primary hover:text-primary/80 underline"
              >
                Gallup
              </a>{" "}
              and{" "}
              <a 
                href="https://www.shrm.org/topics-tools/flagships/all-things-work/employee-recognition-can-go-long-way-but-has-to-be-done-right" 
                target="_blank" 
                rel="noopener noreferrer"
                className="text-primary hover:text-primary/80 underline"
              >
                SHRM
              </a>{" "}
              identifies five principles that make recognition meaningful and sustainable
            </p>
          </div>
          
          <div className="flex flex-col space-y-6">
            {/* First row - 3 pillars */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {pillars.slice(0, 3).map((pillar, index) => {
                const IconComponent = pillar.icon;
                return (
                  <Card key={index} className="border-border/60 bg-card/50 backdrop-blur-sm hover:shadow-md hover:scale-[1.02] transition-all duration-300 group">
                    <CardContent className="p-6 space-y-4 text-center">
                      <div className="w-16 h-16 bg-gradient-to-br from-primary/10 to-primary/5 rounded-xl flex items-center justify-center border border-primary/20 mx-auto group-hover:from-primary/20 group-hover:to-primary/10 transition-all duration-300">
                        <IconComponent className="w-8 h-8 text-primary group-hover:scale-110 transition-transform duration-300" />
                      </div>
                      <div>
                        <h4 className="text-lg font-bold text-foreground mb-3 group-hover:text-primary transition-colors duration-300">{pillar.title}</h4>
                        <p className="text-sm text-muted-foreground leading-relaxed">
                          {pillar.description}
                        </p>
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
            
            {/* Second row - 2 pillars centered */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-4xl mx-auto">
              {pillars.slice(3, 5).map((pillar, index) => {
                const IconComponent = pillar.icon;
                return (
                  <Card key={index + 3} className="border-border/60 bg-card/50 backdrop-blur-sm hover:shadow-md hover:scale-[1.02] transition-all duration-300 group">
                    <CardContent className="p-6 space-y-4 text-center">
                      <div className="w-16 h-16 bg-gradient-to-br from-primary/10 to-primary/5 rounded-xl flex items-center justify-center border border-primary/20 mx-auto group-hover:from-primary/20 group-hover:to-primary/10 transition-all duration-300">
                        <IconComponent className="w-8 h-8 text-primary group-hover:scale-110 transition-transform duration-300" />
                      </div>
                      <div>
                        <h4 className="text-lg font-bold text-foreground mb-3 group-hover:text-primary transition-colors duration-300">{pillar.title}</h4>
                        <p className="text-sm text-muted-foreground leading-relaxed">
                          {pillar.description}
                        </p>
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          </div>
        </section>

        {/* Why Recognition Matters */}
        <section className="space-y-8">
          <div className="text-center space-y-3">
            <h2 className="text-2xl md:text-3xl font-bold text-foreground">
              Why Recognition Matters More Than Ever
            </h2>
            <p className="text-muted-foreground max-w-2xl mx-auto">
              When recognition follows these five principles, the results are powerful
            </p>
          </div>
          
          <Card className="border-border/60 bg-card/50 backdrop-blur-sm">
            <CardContent className="p-8 space-y-6">
              <p className="text-muted-foreground leading-relaxed">
                When recognition follows these five principles, the results are powerful. Employees who feel valued are:
              </p>
              
              <div className="grid sm:grid-cols-3 gap-4">
                {benefits.map((benefit, index) => (
                  <div key={index} className="text-center p-4 bg-primary/5 rounded-lg border border-primary/20">
                    <div className="font-semibold text-foreground">{benefit}</div>
                  </div>
                ))}
              </div>
              
              <p className="text-muted-foreground leading-relaxed">
                The absence of recognition, on the other hand, creates real risks. Studies show that <strong className="text-foreground">79% of employees who leave their jobs cite lack of appreciation as a key reason</strong>. In a tight labor market, failing to recognize employees doesn't just hurt morale-it drives turnover and undermines culture.
              </p>
            </CardContent>
          </Card>
        </section>

        {/* Recognition in Practice */}
        <section className="space-y-8">
          <div className="text-center space-y-3">
            <h2 className="text-2xl md:text-3xl font-bold text-foreground">
              Recognition in Practice
            </h2>
            <p className="text-muted-foreground max-w-2xl mx-auto">
              Recognition doesn't have to be complicated or expensive
            </p>
          </div>
          
          <Card className="border-border/60 bg-card/50 backdrop-blur-sm">
            <CardContent className="p-8 space-y-6">
              <div className="grid sm:grid-cols-2 gap-6">
                {examples.map((example, index) => (
                  <div key={index} className="flex items-start gap-3">
                    <div className="w-2 h-2 bg-primary rounded-full mt-2 shrink-0"></div>
                    <span className="text-muted-foreground text-sm">{example}</span>
                  </div>
                ))}
              </div>
              
              <div className="bg-accent/10 p-6 rounded-lg border border-accent/20">
                <p className="text-muted-foreground leading-relaxed">
                  The important part is that recognition is timely, specific, and visible. A generic "good job" may be polite, but it won't resonate nearly as much as: <em>"Thanks for staying late to help close the client proposal-you kept us on track and it made a huge difference."</em>
                </p>
              </div>
              
              <p className="text-muted-foreground leading-relaxed">
                That's why platforms like{" "}
                <a href="/employee-recognition/peer-to-peer" className="text-primary hover:text-primary/80 underline">
                  Assembly's peer-to-peer recognition
                </a>{" "}
                and{" "}
                <a href="/employee-recognition/milestones" className="text-primary hover:text-primary/80 underline">
                  milestone celebrations
                </a>{" "}
                are designed to make recognition easy, natural, and scalable. By integrating directly into tools your employees already use, Assembly ensures recognition happens in the flow of work-building habits, not one-off gestures.
              </p>
            </CardContent>
          </Card>
        </section>

        {/* FAQ Section */}
        <section className="space-y-8">
          <div className="text-center space-y-3">
            <h2 className="text-2xl md:text-3xl font-bold text-foreground">
              Frequently Asked Questions
            </h2>
            <p className="text-muted-foreground max-w-2xl mx-auto">
              Common questions about employee recognition fundamentals
            </p>
          </div>

          <div className="space-y-4 max-w-4xl mx-auto">
            {[
              {
                question: "What are the 5 pillars of effective employee recognition?",
                answer: "The 5 pillars are: Authentic (genuine and heartfelt), Personalized (tailored to individual preferences), Equitable (accessible to all employees), Embedded (built into daily work), and Frequent (regular and consistent). These principles ensure recognition creates lasting impact."
              },
              {
                question: "What's the difference between recognition and rewards?",
                answer: "Recognition is the acknowledgment of employee contributions and can be verbal praise, written thank-you notes, or public shoutouts. Rewards are tangible benefits like gift cards, bonuses, or experiences. The most effective programs combine both recognition and rewards to create meaningful appreciation."
              },
              {
                question: "How often should employees be recognized?",
                answer: "Employees should receive recognition regularly-ideally weekly or bi-weekly. However, frequency matters less than consistency and specificity. The key is making recognition frequent enough to reinforce positive behaviors and tied to specific actions or outcomes."
              },
              {
                question: "Does employee recognition really improve business results?",
                answer: "Yes, research consistently shows that effective recognition programs improve engagement, reduce turnover, and increase productivity. According to Gallup, employees who receive regular recognition are 4.6 times more likely to be engaged and companies with recognition programs see 31% lower voluntary turnover."
              }
            ].map((faq, index) => (
              <Collapsible key={index}>
                <Card className="border-border/60 bg-card/50 backdrop-blur-sm">
                  <CollapsibleTrigger className="w-full group">
                    <CardHeader className="pb-4">
                      <CardTitle className="text-left flex items-center justify-between">
                        <span className="text-lg font-semibold text-foreground group-hover:text-primary transition-colors">
                          {faq.question}
                        </span>
                        <div className="flex items-center">
                          <Plus className="w-5 h-5 text-muted-foreground group-hover:text-primary transition-colors group-data-[state=open]:hidden" />
                          <Minus className="w-5 h-5 text-muted-foreground group-hover:text-primary transition-colors group-data-[state=closed]:hidden" />
                        </div>
                      </CardTitle>
                    </CardHeader>
                  </CollapsibleTrigger>
                  <CollapsibleContent>
                    <CardContent className="pt-0">
                      <p className="text-muted-foreground leading-relaxed">
                        {faq.answer}
                      </p>
                    </CardContent>
                  </CollapsibleContent>
                </Card>
              </Collapsible>
            ))}
          </div>
        </section>

        {/* Next Steps CTA */}
        <Card className="bg-gradient-to-br from-primary/5 via-accent/5 to-secondary/5 border-primary/20 backdrop-blur-sm">
          <CardContent className="p-8 text-center space-y-6">
            <h3 className="text-2xl font-bold text-foreground">
              Ready to Learn More?
            </h3>
            <p className="text-muted-foreground max-w-2xl mx-auto">
              Now that you understand what recognition is, discover why it's become essential for modern workplaces and how it drives business results.
            </p>
            <Button size="lg" asChild>
              <a href="/employee-recognition-program-guide-assembly/why-is-employee-recognition-important-assembly" className="flex items-center gap-2">
                Learn Why Recognition Matters
                <ArrowRight className="w-4 h-4" />
              </a>
            </Button>
          </CardContent>
        </Card>
      </div>
    </RecognitionGuideLayout>
  );
}